import axios from 'axios'

/**
 * Retry configuration for API requests
 */
export const retryConfig = {
  maxRetries: 3,
  retryDelay: 1000, // Base delay in ms
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
  retryableErrors: ['ECONNABORTED', 'ENOTFOUND', 'ENETUNREACH', 'ETIMEDOUT']
}

/**
 * Checks if an error is retryable
 */
export function isRetryableError(error) {
  // Network errors
  if (!error.response && error.code) {
    return retryConfig.retryableErrors.includes(error.code)
  }

  // HTTP status codes
  if (error.response?.status) {
    return retryConfig.retryableStatusCodes.includes(error.response.status)
  }

  return false
}

/**
 * Calculates exponential backoff delay
 */
export function getRetryDelay(retryCount) {
  const delay = retryConfig.retryDelay * Math.pow(2, retryCount - 1)
  // Add jitter to prevent thundering herd
  const jitter = Math.random() * 0.3 * delay
  return Math.min(delay + jitter, 30000) // Cap at 30 seconds
}

/**
 * Axios interceptor for automatic retry logic
 */
export function createRetryInterceptor(axiosInstance) {
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config

      // Don't retry if disabled for this request
      if (config.noRetry) {
        return Promise.reject(error)
      }

      // Initialize retry count
      if (!config.__retryCount) {
        config.__retryCount = 0
      }

      // Check if we should retry
      if (
        config.__retryCount < retryConfig.maxRetries &&
        isRetryableError(error)
      ) {
        config.__retryCount++

        // Calculate delay with exponential backoff
        const delay = getRetryDelay(config.__retryCount)

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, delay))

        // Retry the request
        return axiosInstance(config)
      }

      return Promise.reject(error)
    }
  )
}

/**
 * Creates a specialized API client with custom configuration
 */
export function createApiClient(options = {}) {
  const {
    baseURL = import.meta.env.VITE_API_URL || '/api',
    timeout = 10000,
    enableRetry = true,
    maxRetries = 3,
    ...axiosConfig
  } = options

  const client = axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json'
    },
    withCredentials: true,
    ...axiosConfig
  })

  // Add retry interceptor if enabled
  if (enableRetry) {
    // Override retry config if custom maxRetries provided
    if (maxRetries !== 3) {
      const originalMaxRetries = retryConfig.maxRetries
      retryConfig.maxRetries = maxRetries
      createRetryInterceptor(client)
      retryConfig.maxRetries = originalMaxRetries
    } else {
      createRetryInterceptor(client)
    }
  }

  return client
}

/**
 * Specialized clients for different use cases
 */

// Long-running operations (file uploads, processing)
export const longRunningClient = createApiClient({
  timeout: 60000, // 60 seconds
  enableRetry: false // Don't retry long operations
})

// Critical operations (auth, payments)
export const criticalClient = createApiClient({
  timeout: 15000, // 15 seconds
  enableRetry: true,
  maxRetries: 5 // More aggressive retry
})

// Background operations (analytics, logging)
export const backgroundClient = createApiClient({
  timeout: 5000, // 5 seconds
  enableRetry: true,
  maxRetries: 2 // Fewer retries for non-critical
})

// Real-time operations (map updates, live data)
export const realtimeClient = createApiClient({
  timeout: 3000, // 3 seconds
  enableRetry: false // No retry for real-time
})
